import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EnumOptions } from '@kinet-open-network/shared/data-access/model';
import { CalendarModule } from 'primeng/calendar';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { TriStateCheckboxModule } from 'primeng/tristatecheckbox';
import { IndicatorComponent } from '../indicator/indicator.component';
import { ResourceTableFilterOptionsPipe } from './resource-table-filter-options.pipe';
import { ResourceTableQueryAdapter } from './resource-table-query-adapter.service';
import { FilterType, TableColumnSchema } from './resource-table.component';

@Component({
  selector: 'kon-resource-table-column-filter',
  standalone: true,
  imports: [
    CommonModule,
    TriStateCheckboxModule,
    InputTextModule,
    InputNumberModule,
    CalendarModule,
    TableModule,
    MultiSelectModule,
    IndicatorComponent,
    CalendarModule,
    FormsModule,
    ResourceTableFilterOptionsPipe,
  ],
  template: `
    <ng-container *ngIf="column() as column">
      <!-- SELECT FILTER -->
      <p-columnFilter
        *ngIf="type() === 'select'; else basicFilter"
        matchMode="in"
        display="menu"
        [showMatchModes]="false"
        [showOperator]="false"
        [showAddButton]="false"
        [field]="column.key"
      >
        <ng-template pTemplate="filter" let-value let-filter="filterCallback">
          <p-multiSelect
            *ngIf="options() as options"
            [ngModel]="value"
            [options]="options | resourceTableFilterOptions : column.filter"
            [placeholder]="label()"
            (onChange)="filter($event.value)"
            optionLabel="label"
            optionValue="value"
          >
            <ng-template let-option pTemplate="item">
              <kon-indicator
                *ngIf="
                  isEnumOptions(options) && hasColor(options);
                  else basicOption
                "
                [options]="options"
                [value]="option.value"
              />

              <ng-template #basicOption>
                {{ option.label }}
              </ng-template>
            </ng-template>
          </p-multiSelect>
        </ng-template>
      </p-columnFilter>
      <!-- END SELECT FILTER -->

      <!-- BASIC FILTER -->
      <ng-template #basicFilter>
        <p-columnFilter
          [type]="type()!"
          [matchMode]="matchmode()"
          [field]="column.key"
          [display]="'menu'"
          [operator]="columnFilterOperator(column)"
          [hideOnClear]="true"
        >
          <ng-template
            pTemplate="filter"
            let-filter="filterCallback"
            let-filterConstraint="filterConstraint"
            let-placeholder="placeholder"
            let-type="type"
            let-minFractionDigits="minFractionDigits"
            let-maxFractionDigits="maxFractionDigits"
            let-prefix="prefix"
            let-suffix="suffix"
            let-locale="locale"
            let-localeMatcher="localeMatcher"
            let-currency="currency"
            let-currencyDisplay="currencyDisplay"
            let-useGrouping="useGrouping"
            let-showButtons="showButtons"
          >
            @if(type === 'boolean' || filterConstraint?.matchMode ===
            'present'){
            <p-triStateCheckbox
              *ngIf="
                type === 'boolean' || filterConstraint?.matchMode === 'present'
              "
              [ngModel]="filterConstraint?.value"
              (ngModelChange)="filter($event)"
              [label]="
                filterConstraint?.value === true
                  ? 'Áno'
                  : filterConstraint?.value === false
                  ? 'Nie'
                  : '-'
              "
            ></p-triStateCheckbox
            >}@else if (type === 'text') {
            <input
              type="text"
              pInputText
              [value]="filterConstraint?.value"
              (input)="filterConstraint.value = getEventValue($event)"
              (keydown.enter)="
                filter(getEventValue($event)) && $event.preventDefault()
              "
              [attr.placeholder]="placeholder"
            />
            } @else if (type === 'numeric') {
            <p-inputNumber
              [ngModel]="filterConstraint?.value"
              (ngModelChange)="filterConstraint.value = $event"
              (onKeyDown)="
                $event.key === 'Enter' &&
                  filter(getEventValue($event)) &&
                  $event.preventDefault()
              "
              [showButtons]="showButtons"
              [minFractionDigits]="minFractionDigits"
              [maxFractionDigits]="maxFractionDigits"
              [prefix]="prefix"
              [suffix]="suffix"
              [placeholder]="placeholder"
              [mode]="currency ? 'currency' : 'decimal'"
              [locale]="locale"
              [localeMatcher]="localeMatcher"
              [currency]="currency"
              [currencyDisplay]="currencyDisplay"
              [useGrouping]="useGrouping"
            ></p-inputNumber>
            } @else if (type === 'date') {
            <p-calendar
              [placeholder]="placeholder"
              [ngModel]="filterConstraint?.value"
              (ngModelChange)="filter($event)"
              appendTo="body"
            ></p-calendar>
            }
          </ng-template>
        </p-columnFilter>
      </ng-template>
      <!-- END BASIC FILTER -->
    </ng-container>
  `,
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ResourceTableQueryAdapter],
})
export class ResourceTableColumnFilterComponent {
  column = input<TableColumnSchema>();
  type = input<FilterType>();
  label = input<string>();
  matchmode = input<string>();
  options = input<
    | {
        label: string;
        value: any;
      }[]
    | Map<string, EnumOptions>
  >();
  @Input() columnFilterOperator!: (column: TableColumnSchema) => string;

  getEventValue(event: Event) {
    return (event.target as HTMLInputElement).value;
  }

  hasColor(options: Map<string, EnumOptions>): boolean {
    return [...options.values()].some((value) => value.color);
  }

  isEnumOptions(options: any): options is Map<string, EnumOptions> {
    return options instanceof Map;
  }
}
